import React, {
  type Dispatch,
  type SetStateAction,
  useEffect,
  useRef,
  useState,
  type JSX,
} from 'react';
import TalentTreeFull, {
  type TalentTreeFullProps,
} from '@resources/js/components/TalentTreeFull/TalentTreeFull';
import { type RetailWarcraftTalentTreeBuildNode } from '@resources/js/typings/viewModels/retailWarcraftTalentTreeBuildNode';
import useGame from '@resources/js/contexts/useGame';
import useScript from '@resources/js/hooks/useScript';
import VerticalContent from '@resources/js/components/VerticalContent/VerticalContent';
import EnhancedMarkdown from '@resources/js/components/EnhancedMarkdown/EnhancedMarkdown';
import useTalentTreeBlueprint from '@resources/js/contexts/useTalentTreeBlueprint';
import HorizontalContent from '@resources/js/components/HorizontalContent/HorizontalContent';
import Table from '@resources/js/components/Table/Table';
import TalentTreeNode from '@resources/js/components/TalentTreeNode/TalentTreeNode';
import Button from '@resources/js/components/Button/Button';
import {
  BuildsMetricTile,
  type BuildsMetricTileViewModel,
} from '@resources/js/components/BuildsMetricTiles/BuildsMetricTiles';
import { talentTreeDifferences } from '@resources/js/helpers/buildsTalentTrees';
import BuildsNoDataPlaceholder from '@resources/js/components/BuildsNoDataPlaceholder/BuildsNoDataPlaceholder';
import ScreenReaderOnly from '../ScreenReaderOnly/ScreenReaderOnly';
import Body from '@resources/js/components/Body/Body';
import Heading from '@resources/js/components/Heading/Heading';
import buildsEnhancedMarkdownOverrides from '@resources/js/helpers/buildsEnhancedMarkdownOverrides';
import DefaultLinkWrapper, {
  type LinkWrapper,
} from '@resources/js/components/LinkWrapper/LinkWrapper';
import { BuildsLargeLinkButton } from '@resources/js/components/BuildsLargeLinkButton/BuildsLargeLinkButton';
import SiteLogo from '@resources/js/components/SiteLogo/SiteLogo';

export type BuildsTalentTreeBuildAlternativeViewModel = {
  title: string;
  popularity: string | number;
  reportUrl: string;
  talentTree: TalentTreeFullProps;
  isDefaultSelection: boolean;
  keystoneLevel: number | null;
};

export type BuildsTalentTreeBuildSetViewModel = {
  title?: string;
  metricTiles: BuildsMetricTileViewModel[];
  alternatives: BuildsTalentTreeBuildAlternativeViewModel[];
};

export type BuildsTalentTreeBuildSectionProps = {
  title: string;
  description: string;
  link?: { label: string; url: string };
  alternativesTitle: string;
  alternativesDescription: string;
  popularityLabel: string;
  addedTalentsLabel: string;
  removedTalentsLabel: string;
  currentlySelectedLabel: string;
  alternativeTreeTitleLabel: string;
  talentTreeBuildSets: BuildsTalentTreeBuildSetViewModel[];
  LinkWrapper?: LinkWrapper;
  topLogLabel: string;
  openReportLabel: string;
  maxKeyLabel: string;
  openReportButtonBackgroundImageUrl: string;
};

type ChangedTalentsCellProps = {
  talents: RetailWarcraftTalentTreeBuildNode[];
  spellModifierId: number;
};

const ChangedTalentsCell = ({
  talents,
  spellModifierId,
}: ChangedTalentsCellProps): JSX.Element => {
  return (
    <HorizontalContent
      className={'builds-talent-tree-build-section__changed-talents'}
    >
      {talents.map((node) => (
        <TalentTreeNode
          key={`${node.node.nodeId}-entry`}
          node={node}
          renderMultiplePointsIndicator={false}
          showChoices={false}
          spellModifierId={spellModifierId}
        />
      ))}
    </HorizontalContent>
  );
};

type InteriorProps = Omit<
  BuildsTalentTreeBuildSectionProps,
  'talentTreeBlueprints' | 'talentTreeBuildSets'
> & {
  selected?: BuildsTalentTreeBuildAlternativeViewModel;
  setSelectedIndex: Dispatch<SetStateAction<number>>;
  buildSet?: BuildsTalentTreeBuildSetViewModel;
};

/**
 * Interior component for rendering the most popular talent tree. This is re-used by the `WithAlternatives` variant section.
 *
 * The wrapping component is expected to set up the blueprint context and provide talent tree selection state.
 */
export function BuildsTalentTreeBuildSectionInterior({
  LinkWrapper = DefaultLinkWrapper,
  ...props
}: InteriorProps): JSX.Element {
  const {
    title,
    description,
    topLogLabel,
    link,
    selected,
    buildSet,
    openReportLabel,
    openReportButtonBackgroundImageUrl,
  } = props;
  return (
    <VerticalContent className='builds-talent-tree-build-section'>
      <Body>
        <div className={'builds-talent-tree-build-section__heading-and-link'}>
          <Heading level={2}>
            <EnhancedMarkdown markdown={title} wrapped={false} />
          </Heading>
          {link && <LinkWrapper href={link.url}>{link.label}</LinkWrapper>}
        </div>
        <p className={'hide-on-compact'}>
          <EnhancedMarkdown markdown={description} wrapped={false} />
        </p>
      </Body>
      {buildSet && selected?.talentTree ? (
        <>
          <div
            className={'builds-talent-tree-build-section__metrics-and-links'}
          >
            {buildSet.metricTiles.map((buildsMetricTile) => (
              <BuildsMetricTile
                key={buildsMetricTile.label}
                {...buildsMetricTile}
              />
            ))}

            {selected.reportUrl && (
              <BuildsLargeLinkButton
                caption={topLogLabel}
                text={openReportLabel}
                isExternal={true}
                href={selected.reportUrl}
                backgroundImageUrl={openReportButtonBackgroundImageUrl}
              />
            )}
          </div>
          <div className='builds-talent-tree-build-section__talent-trees'>
            <TalentTreeFull {...selected?.talentTree} />
            <BuildsTalentTreeAlternateClassTreesTable {...props} />
          </div>
        </>
      ) : (
        <BuildsNoDataPlaceholder />
      )}
    </VerticalContent>
  );
}

export function BuildsTalentTreeAlternateClassTreesTable({
  popularityLabel,
  addedTalentsLabel,
  removedTalentsLabel,
  alternativesTitle,
  alternativesDescription,
  currentlySelectedLabel,
  setSelectedIndex,
  buildSet,
  selected,
  alternativeTreeTitleLabel,
  openReportLabel,
  maxKeyLabel,
}: Omit<InteriorProps, 'title' | 'description'>): JSX.Element | null {
  const talentTreeBlueprint = useTalentTreeBlueprint(
    selected?.talentTree.dehydratedBuild.changeSet,
  );

  if (!buildSet || !selected) {
    return null;
  }

  return (
    <VerticalContent
      className={'builds-talent-tree-build-section__talent-tree-alternatives'}
    >
      <EnhancedMarkdown
        markdown={`
## ${alternativesTitle}

${alternativesDescription}
`}
        overrides={buildsEnhancedMarkdownOverrides}
      />
      <Table
        style={'v2-bordered'}
        columns={{
          title: {
            header: (
              <ScreenReaderOnly>{alternativeTreeTitleLabel}</ScreenReaderOnly>
            ),
          },
          ...(selected.keystoneLevel !== null
            ? {
                maxKey: {
                  header: maxKeyLabel ?? '',
                  textAlign: 'right',
                },
              }
            : {}),
          popularity: {
            header: popularityLabel,
            textAlign: 'right',
          },
          addedTalents: {
            header: addedTalentsLabel,
            textAlign: 'right',
            noWrap: true,
          },
          removedTalents: {
            header: removedTalentsLabel,
            textAlign: 'right',
            noWrap: true,
          },
        }}
        onRowClick={(row) => setSelectedIndex(row.original.index as number)}
        data={buildSet.alternatives.map((talentTreeBuildAlternative, index) => {
          const { addedTalents, removedTalents } = talentTreeDifferences(
            talentTreeBlueprint,
            selected.talentTree,
            talentTreeBuildAlternative.talentTree,
          );

          return {
            index,
            title: (
              <Button styles={['unstyled']}>
                {talentTreeBuildAlternative.title}
              </Button>
            ),
            maxKey:
              talentTreeBuildAlternative.keystoneLevel !== null
                ? '+' + talentTreeBuildAlternative.keystoneLevel
                : '',
            popularity: (
              <HorizontalContent
                gap='smaller'
                className={'builds-talent-tree-build-section__popularity'}
              >
                <div>{talentTreeBuildAlternative.popularity}</div>
                {talentTreeBuildAlternative.reportUrl ? (
                  <a
                    href={talentTreeBuildAlternative.reportUrl}
                    target={'_blank'}
                    className={'builds-talent-tree-build-section__report_link'}
                    rel='noreferrer'
                  >
                    <SiteLogo size={20} alt={openReportLabel} />
                  </a>
                ) : null}
              </HorizontalContent>
            ),
            addedTalents:
              talentTreeBuildAlternative.talentTree === selected.talentTree ? (
                <span
                  className={
                    'builds-talent-tree-build-section__talent-tree-alternatives--selected'
                  }
                >
                  {currentlySelectedLabel}
                </span>
              ) : (
                <ChangedTalentsCell
                  talents={addedTalents}
                  spellModifierId={talentTreeBlueprint?.spellModifierId ?? 0}
                />
              ),
            removedTalents:
              talentTreeBuildAlternative.talentTree === selected.talentTree ? (
                <span
                  className={
                    'builds-talent-tree-build-section__talent-tree-alternatives--selected'
                  }
                >
                  {currentlySelectedLabel}
                </span>
              ) : (
                <ChangedTalentsCell
                  talents={removedTalents}
                  spellModifierId={talentTreeBlueprint?.spellModifierId ?? 0}
                />
              ),
          };
        })}
      />
    </VerticalContent>
  );
}

function findInitialSelection(
  alternatives: BuildsTalentTreeBuildSetViewModel['alternatives'],
): number {
  const defaultSelection = alternatives.findIndex(
    (alt) => alt.isDefaultSelection,
  );

  return defaultSelection > -1 ? defaultSelection : 0;
}

export function useTreeSelection(
  alternatives: BuildsTalentTreeBuildSectionProps['talentTreeBuildSets'][number]['alternatives'],
): [number, Dispatch<SetStateAction<number>>] {
  const initialRenderRef = useRef(true);
  const [selectedIndex, setSelectedIndex] = useState(
    findInitialSelection(alternatives),
  );

  useEffect(() => {
    if (initialRenderRef.current) {
      initialRenderRef.current = false;
      return;
    }

    setSelectedIndex(findInitialSelection(alternatives));
  }, [alternatives]);

  return [selectedIndex, setSelectedIndex];
}

export default function BuildsTalentTreeBuildSection(
  props: BuildsTalentTreeBuildSectionProps,
): JSX.Element {
  const game = useGame();
  useScript(game.tooltipLibraryPath);

  const {
    talentTreeBuildSets: [recommended],
  } = props;

  const [selectedIndex, setSelectedIndex] = useTreeSelection(
    recommended?.alternatives ?? [],
  );

  const selected = recommended?.alternatives[selectedIndex];

  return (
    <BuildsTalentTreeBuildSectionInterior
      {...props}
      selected={selected}
      setSelectedIndex={setSelectedIndex}
      buildSet={recommended}
    />
  );
}
